import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { useAuth } from 'src/shared/hooks/authProvider'
import { useToast } from 'src/shared/hooks/use-toast'
import { Database } from 'supabase/database.types'
import {
  ORG_USER_QUERY_STALE_MS,
  fetchOrgUserRow,
  orgUserQueryKey,
} from './orgUserQuery'
import { queryKeys } from './queryKeys'

export type InsertUser = Database['public']['Tables']['user']['Insert']
export type InsertOrgUser = Database['public']['Tables']['org_user']['Insert']
export type OrgUser_ = Database['public']['Tables']['org_user']['Row']
export type User_ = Database['public']['Tables']['user']['Row']

export const useOrgUsersApi = (org_id_?: string) => {
  const { session } = useAuth()
  const queryClient = useQueryClient()
  const supabase = useSupabaseClient()
  const { toast } = useToast()

  const { data: orgUser } = useQuery({
    queryKey: session?.user?.id
      ? orgUserQueryKey(session.user.id)
      : [queryKeys.org_user, undefined],
    queryFn: () => fetchOrgUserRow(supabase, session!.user!.id),
    enabled: !!session?.user?.id,
    staleTime: ORG_USER_QUERY_STALE_MS,
  })

  const effectiveOrgForMembers =
    org_id_ || orgUser?.data?.organization_id || null

  // Either use the org from prop or current user
  const { data: orgUsers } = useQuery({
    queryKey: [queryKeys.org_users, effectiveOrgForMembers],
    queryFn: async () => {
      const filterOrgId = org_id_ || orgUser?.data?.organization_id!
      return await supabase
        .from('org_user')
        .select(`*, user!org_user_user_id_fkey(*), role(*), organization(*)`)
        .filter('organization_id', 'eq', filterOrgId)
    },
    enabled: !!org_id_ || !!orgUser?.data?.organization_id,
    staleTime: ORG_USER_QUERY_STALE_MS,
  })

  const updateUser = useMutation({
    mutationFn: async (insertUser: InsertUser) =>
      await supabase.from('user').update(insertUser).eq('id', insertUser.id!),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [queryKeys.org_user],
      })
      queryClient.invalidateQueries({
        queryKey: [queryKeys.org_users],
      })
    },
  })

  const updateOrgUser = useMutation({
    mutationFn: async (insertOrgUser: InsertOrgUser) =>
      await supabase
        .from('org_user')
        .update(insertOrgUser)
        .eq('id', insertOrgUser.id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.org_users] })
    },
  })

  const createOrgUser = useMutation({
    mutationFn: async (insertOrgUser: InsertOrgUser) =>
      await supabase.from('org_user').insert(insertOrgUser),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.org_users] })
      toast({
        variant: 'success',
        title: 'Created org user',
      })
    },
  })

  const deleteOrgUser = useMutation({
    mutationFn: async ({ id }: { id: number }) => {
      const { error } = await supabase.from('org_user').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKeys.org_users] })
      toast({
        variant: 'success',
        title: 'Removed user from organization',
      })
    },
    onError: (error) => {
      toast({
        variant: 'destructive',
        title: 'Failed to remove user',
        description: error instanceof Error ? error.message : undefined,
      })
    },
  })

  return {
    orgUser,
    orgUsers,
    updateUser,
    updateOrgUser,
    createOrgUser,
    deleteOrgUser,
  }
}
